import { ChangeEvent, FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import Select, { Option } from '@ingka/select';
import ToggleControlWrapper from '@ingka/toggle';

import { DropdownDatePicker } from '@/components/controls/dropdown-datepicker';
import { useChartContext } from './context';
import {
  CHART_GRANULARITY,
  CHART_INTERVAL,
  DateInterval,
  PREV_WEEK,
} from './constants';

const formatMillis = (millis: number) =>
  DateTime.fromMillis(millis).toFormat('MM/dd/yy');

const useFormatDatePickerLabel = (
  interval: DateInterval,
  from: number,
  to: number
) => {
  const { t } = useTranslation();

  return interval === 'custom'
    ? `${formatMillis(from)} - ${formatMillis(to)}`
    : t('label.dateRange');
};

const GranularityFilter = () => {
  const { t } = useTranslation();
  const { granularity, setGranularity } = useChartContext();
  const handleGranularity = (e: any, index: number) => {
    setGranularity(CHART_GRANULARITY[index].value);
  };
  const granularityIndex = CHART_GRANULARITY.findIndex(
    ({ value }) => value === granularity
  );

  return (
    <ToggleControlWrapper
      activeIndex={granularityIndex}
      onClick={handleGranularity}
      buttons={CHART_GRANULARITY.map(({ title }) => ({ text: t(title) }))}
    />
  );
};

const IntervalFilter = () => {
  const { t } = useTranslation();
  const { date, setDate } = useChartContext();
  const [interval, setInterval] = useState<DateInterval>('last30Days');
  const handleInterval = (e: ChangeEvent<HTMLSelectElement>) => {
    const selectedInterval = e.target.value as DateInterval;
    const intervalData = CHART_INTERVAL.find(
      ({ value }) => value === selectedInterval
    );

    if (!intervalData) return;

    setInterval(selectedInterval);
    setDate(intervalData.date);
  };
  const handleDate = (dates?: { from?: number; to?: number }) => {
    const from = dates?.from || PREV_WEEK.toMillis();

    setDate({
      from,
      to: dates?.to || from,
    });
  };
  const customDateLabel = useFormatDatePickerLabel(
    interval,
    date.from,
    date.to
  );

  return (
    <div className="flex gap-6">
      <Select
        id="selectDates"
        onChange={handleInterval}
        value={interval}
        required
      >
        {CHART_INTERVAL.map(({ title, value }) => (
          <Option key={value} value={value} name={t(title)} />
        ))}
      </Select>

      {interval === 'custom' && (
        <DropdownDatePicker
          label={customDateLabel}
          value={date}
          onChange={handleDate}
          confirm
          dayPicker={{
            fromDate: new Date(2021, 3, 17), // When QR 2.0 was released,
            toDate: new Date(), // Today
          }}
        />
      )}
    </div>
  );
};

export const ChartFilters: FC = () => {
  return (
    <>
      <IntervalFilter />

      <GranularityFilter />
    </>
  );
};
